import { chartLayoutColours } from '@risksmart-app/components/src/utils/colours';
import Highcharts from 'highcharts';
import more from 'highcharts/highcharts-more';
import exporting from 'highcharts/modules/exporting';
import heatmap from 'highcharts/modules/heatmap';
import offlineExporting from 'highcharts/modules/offline-exporting';
import type {
  HighchartsReactProps,
  HighchartsReactRefObject,
} from 'highcharts-react-official';
import HighchartsReact from 'highcharts-react-official';
import type { ForwardedRef } from 'react';
import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { handleError } from 'src/utils/errorUtils';

import { useMergeChartOptions } from './hooks/useMergeChartOptions';

type RSHighchartsProps = {
  options: Highcharts.Options;
  containerProps: HighchartsReactProps['containerProps'];
  ref: ForwardedRef<HighchartsReactRefObject>;
};

// Todo: Figure out how to get working with styledMode and style modules
// Font family must be set globally for Highcharts to apply correctly
Highcharts.setOptions({
  chart: {
    style: {
      fontFamily: 'Sora, sans-serif',
    },
  },
});

export const RSHighcharts = forwardRef<
  HighchartsReactRefObject,
  RSHighchartsProps
>(({ options, containerProps }, ref) => {
  const internalRef = useRef<HighchartsReactRefObject | null>(null);

  const setRefs = useCallback(
    (instance: HighchartsReactRefObject | null) => {
      internalRef.current = instance;
      if (typeof ref === 'function') {
        ref(instance);
      } else if (ref) {
        ref.current = instance;
      }
    },
    [ref]
  );

  // Highcharts' built-in window-resize reflow is disabled below (`reflow: false`).
  // Observe the container so the chart reflows on any size change. Reflow is
  // deferred to the next animation frame so a burst of resize events coalesces
  // into one reflow against the settled container size — calling reflow
  // synchronously inside the observer callback during a fast drag can
  // otherwise leave the chart locked to an intermediate size.
  useEffect(() => {
    const container = internalRef.current?.container?.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      return;
    }
    let pendingFrame: number | null = null;
    const observer = new ResizeObserver(() => {
      if (pendingFrame !== null) {
        cancelAnimationFrame(pendingFrame);
      }
      pendingFrame = requestAnimationFrame(() => {
        pendingFrame = null;
        internalRef.current?.chart?.reflow();
      });
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      if (pendingFrame !== null) {
        cancelAnimationFrame(pendingFrame);
      }
    };
  }, []);

  useEffect(() => {
    try {
      // Initialize exporting modules - this is the documented method of loading modules
      if (typeof exporting === 'function') {
        // @ts-expect-error
        exporting(Highcharts);
      }
      if (typeof offlineExporting === 'function') {
        // @ts-expect-error
        offlineExporting(Highcharts);
      }
      if (typeof more === 'function') {
        // @ts-expect-error
        more(Highcharts);
      }
    } catch {
      handleError(`Unable to load modules for Highcharts`);
    }
  }, []);

  useEffect(() => {
    if (options.chart?.type === 'heatmap' && typeof heatmap === 'function') {
      try {
        // @ts-expect-error
        heatmap(Highcharts);
      } catch {
        handleError(`Unable to load heatmap module for Highcharts`);
      }
    }
  }, [options.chart?.type]);

  const defaults: Highcharts.Options = {
    credits: {
      enabled: false,
    },
    xAxis: {
      labels: {
        style: {
          color: chartLayoutColours.axisLabels,
        },
      },
    },
    yAxis: {
      labels: {
        style: {
          color: chartLayoutColours.axisLabels,
        },
      },
    },
    chart: {
      // Disable Highcharts' built-in window-resize reflow; the ResizeObserver
      // above handles container size changes from any source.
      reflow: false,
    },
  };

  const mergedOptions = useMergeChartOptions(defaults, options);

  return (
    <HighchartsReact
      ref={setRefs}
      highcharts={Highcharts}
      options={mergedOptions}
      containerProps={containerProps}
    />
  );
});
